import catchWrapper from 'common/utils/catchWrapper'

import tripleWhaleInitSnippet from './tripleWhaleInitSnippet'

const buildLineItem = (productId, quantity, variantId) => ({
  i: externalId(productId.toString()),
  q: quantity,
  v: externalId(variantId || productId).toString(),
})

/**
 * Initialize TripleWhale pixel. Also should trigger page event
 *
 * @param {string, number} productId
 * @returns {void}
 */
export const tripleWhaleInit = (productId) => {
  if (typeof window === 'undefined') return

  const normalizedId = productId?.toString()
  const domain = window.app.tripleWhaleAccounts?.find(({ productIds }) => productIds.includes(normalizedId))?.domain
  if (!domain) return

  tripleWhaleInitSnippet(domain)
  window.addEventListener(
    'CookiebotOnConsentReady',
    function () {
      TriplePixel('trackingConsent', Cookiebot.consent.marketing)
    },
    false,
  )
}

/**
 * @param {string,null} email - at least one is required
 * @param {string,null} phone
 * @returns
 */
export const tripleWhaleContact = ({ email, phone }) =>
  catchWrapper(() => {
    if (!window.TriplePixel) return

    TriplePixel('Contact', { email, phone })
  })

export const tripleWhalePageLoad = () =>
  catchWrapper(() => {
    if (!window.TriplePixel) return

    TriplePixel('pageLoad')
  })

export const tripleWhaleCheckoutStarted = ({ email, productId, variantId }) =>
  catchWrapper(() => {
    if (!window.TriplePixel) return

    TriplePixel('CheckoutStarted', {
      email: email || undefined,
      lineItems: [buildLineItem(productId, 1, variantId)],
    })
  })

export const tripleWhaleAddToCart = ({ productId, quantity = 1, variantId }) =>
  catchWrapper(() => {
    if (!window.TriplePixel) return

    const item = buildLineItem(productId, quantity, variantId)
    TriplePixel('AddToCart', { item: item.i, q: item.q, v: item.v })
  })

export const tripleWhalePurchase = (
  eventId,
  { email, phone, name, orderId, quantity, productId, variantId, address = {} },
) =>
  catchWrapper(() => {
    if (!window.TriplePixel || !eventId || !productId) return

    const nameParts = (name || '').split(' ')
    TriplePixel('Purchase', {
      eventId, // identifier for cross-platform dedupe
      email,
      phone,
      firstName: nameParts[0],
      lastName: nameParts.length > 1 ? nameParts[nameParts.length - 1] : '',
      orderId: externalId(orderId),
      lineItems: [buildLineItem(productId, quantity, variantId)],
      address: {
        zip: address.shipping_zip,
        city: address.shipping_city,
        countryCode: address.shipping_country,
        provinceCode: address.shipping_state,
      },
    })
  })

/**
 * Decorates give id to be unique and identifiable across different environments
 *
 * @param {string, number} id
 * @returns {string}
 */
export const externalId = (id) => {
  if (window.app.published) return id

  return `${window.app.releaseHash.slice(0, 12)}-${id}`
}
